/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  Globe,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

const INDUSTRY_ICONS = [
  'Building2', 'Landmark', 'Stethoscope', 'Plane', 'Shield', 'Car',
  'GraduationCap', 'ShoppingBag', 'Briefcase', 'Truck', 'Scale', 'HeartHandshake'
];

export default function IndustriesManager() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [generatingAi, setGeneratingAi] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    shortDesc: '',
    fullDescription: '',
    icon: 'Building2',
    imageUrl: '',
    order: 0,
    seoTitle: '',
    metaDescription: '',
    keywords: '',
  });

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await axiosClient.get('/industries');
      setIndustries(res.data);
    } catch (err) {
      toast.error('Failed to load industries.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    setActiveTab('general');
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        slug: item.slug,
        shortDesc: item.shortDesc || '',
        fullDescription: item.fullDescription || '',
        icon: item.icon || 'Building2',
        imageUrl: item.imageUrl || '',
        order: item.order || 0,
        seoTitle: item.seoTitle || '',
        metaDescription: item.metaDescription || '',
        keywords: item.keywords || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        slug: '',
        shortDesc: '',
        fullDescription: '',
        icon: 'Building2',
        imageUrl: '',
        order: industries.length,
        seoTitle: '',
        metaDescription: '',
        keywords: '',
      });
    }
    setModalOpen(true);
  };

  const handleTitleChange = (val) => {
    setFormData(prev => ({
      ...prev,
      title: val,
      slug: prev.slug && editingItem ? prev.slug : val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    }));
  };

  const handleAiGenerate = async () => {
    if (!formData.title) {
      toast.error('Please enter an Industry Title first.');
      return;
    }
    setGeneratingAi(true);
    try {
      const res = await axiosClient.post('/ai/generate', {
        action: 'generate_seo',
        prompt: `IT solutions for ${formData.title} industry`,
      });
      const data = JSON.parse(res.data.output);
      setFormData(prev => ({
        ...prev,
        seoTitle: data.seoTitle || prev.seoTitle,
        metaDescription: data.metaDescription || prev.metaDescription,
        keywords: data.keywords || prev.keywords,
      }));
      toast.success('AI SEO suggestions applied!');
    } catch (err) {
      toast.error('Failed to generate AI suggestions.');
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) {
      toast.error('Title and Slug are required.');
      return;
    }

    try {
      if (editingItem) {
        await axiosClient.put(`/industries/${editingItem.id}`, formData);
        toast.success('Industry updated successfully.');
      } else {
        await axiosClient.post('/industries', formData);
        toast.success('Industry created successfully.');
      }
      setModalOpen(false);
      fetchIndustries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save industry.');
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete the "${title}" industry?`)) {
      try {
        await axiosClient.delete(`/industries/${id}`);
        toast.success('Industry deleted.');
        fetchIndustries();
      } catch (err) {
        toast.error('Failed to delete industry.');
      }
    }
  };

  const filteredIndustries = industries.filter(ind =>
    ind.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ind.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ind.shortDesc && ind.shortDesc.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <Building2 className="text-blue-500" />
            <span>Industries CMS Management</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage industry verticals, tailored IT solutions, and vertical SEO landing pages.
          </p>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all self-start sm:self-auto"
        >
          <Plus size={16} />
          <span>Add New Industry</span>
        </button>
      </div>

      {/* Filter and Stats Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search industries by name, slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="text-xs text-slate-400 font-medium">
          Showing <span className="text-white font-bold">{filteredIndustries.length}</span> of {industries.length} Industries
        </div>
      </div>

      {/* Industries Grid List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : filteredIndustries.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 rounded-2xl border border-slate-800/60">
          <Building2 className="mx-auto text-slate-600 mb-3" size={40} />
          <h3 className="text-sm font-bold text-slate-300">No industries found</h3>
          <p className="text-xs text-slate-500 mt-1">Get started by creating your first industry sector.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredIndustries.map((item) => (
            <div
              key={item.id}
              className="bg-slate-900 border border-slate-800/80 hover:border-slate-700 rounded-2xl p-5 shadow-xl transition-all duration-200 flex flex-col justify-between group"
            >
              <div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center font-bold">
                      <Building2 size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-white group-hover:text-blue-400 transition-colors">
                        {item.title}
                      </h3>
                      <span className="text-[11px] text-slate-400 font-mono">/industries/{item.slug}</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-300 line-clamp-3 leading-relaxed mb-4">
                  {item.shortDesc || 'No summary description provided yet.'}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <a
                  href={`/industries/${item.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-blue-400 flex items-center space-x-1 transition-colors"
                >
                  <span>Preview Page</span>
                  <ExternalLink size={12} />
                </a>

                <div className="flex items-center space-x-1.5">
                  <button
                    onClick={() => handleOpenModal(item)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-blue-600/20 text-slate-300 hover:text-blue-400 transition-colors"
                    title="Edit Industry"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.title)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-600/20 text-slate-300 hover:text-rose-400 transition-colors"
                    title="Delete Industry"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Create Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-white">
                  {editingItem ? `Edit: ${editingItem.title}` : 'Create New Industry'}
                </h2>
                <p className="text-xs text-slate-400">Configure industry details, content copy, and SEO parameters</p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Tabs */}
            <div className="flex border-b border-slate-800 px-6 bg-slate-950/40">
              <button
                onClick={() => setActiveTab('general')}
                className={`py-3 px-4 text-xs font-bold border-b-2 transition-all ${
                  activeTab === 'general'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                General Details
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center space-x-1.5 ${
                  activeTab === 'seo'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <Globe size={14} />
                <span>SEO & Meta Tags</span>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4 flex-1 custom-scrollbar">
              {activeTab === 'general' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Industry Title *</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="e.g. Healthcare & Medical Practices"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Slug (URL identifier) *</label>
                      <input
                        type="text"
                        required
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        placeholder="e.g. healthcare-and-medical-practices"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Icon Identifier</label>
                      <select
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                      >
                        {INDUSTRY_ICONS.map(ic => (
                          <option key={ic} value={ic}>{ic}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Image / Banner URL</label>
                      <input
                        type="text"
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        placeholder="https://images.unsplash.com/..."
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">Short Summary Description</label>
                    <textarea
                      rows={2}
                      value={formData.shortDesc}
                      onChange={(e) => setFormData({ ...formData, shortDesc: e.target.value })}
                      placeholder="Brief overview of how FAJTech delivers IT services for this sector..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">Full Detailed Description</label>
                    <textarea
                      rows={4}
                      value={formData.fullDescription}
                      onChange={(e) => setFormData({ ...formData, fullDescription: e.target.value })}
                      placeholder="Comprehensive industry solutions, regulatory compliance (HIPAA, SOC 2), and technical specs..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-blue-950/30 border border-blue-800/40 p-3.5 rounded-xl">
                    <div>
                      <span className="text-xs font-bold text-blue-300">AI SEO Assistant</span>
                      <p className="text-[11px] text-slate-400">Generate high-ranking meta title, description, and keywords automatically.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAiGenerate}
                      disabled={generatingAi}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-md"
                    >
                      <Sparkles size={13} />
                      <span>{generatingAi ? 'Generating...' : 'Auto-Generate'}</span>
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">SEO Meta Title</label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
                      placeholder="e.g. Healthcare IT Solutions & HIPAA Compliance | FAJTech"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">Meta Description (SERP snippet)</label>
                    <textarea
                      rows={3}
                      value={formData.metaDescription}
                      onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                      placeholder="Max 160 characters describing the industry page for Google search..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">Keywords (Comma separated)</label>
                    <input
                      type="text"
                      value={formData.keywords}
                      onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                      placeholder="e.g. Healthcare IT, HIPAA compliance, Medical Practice Cybersecurity"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="pt-4 border-t border-slate-800 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all"
                >
                  <Check size={16} />
                  <span>{editingItem ? 'Save Changes' : 'Create Industry'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
